import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import ModuleLayout from '../components/ModuleLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  MessageCircle, Loader2, Copy, Check, ExternalLink, Power, PowerOff,
  Palette, RefreshCw, Share2, QrCode, Download, Send, ChefHat, Sparkles,
  BarChart3, Upload, Image, ToggleLeft, ToggleRight, MessageSquare, ShoppingCart, TrendingUp, Mic, Volume2,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';
import QRCode from 'react-qr-code';
import { RichCardRenderer } from '../components/RichCards';
import CustomerPreviewModal from '../components/CustomerPreviewModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

export default function ConversationalBotPage() {
  const { authHeaders, activeBranch } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [siteData, setSiteData] = useState(null);
  const [exists, setExists] = useState(false);
  const [copied, setCopied] = useState(false);
  const [name, setName] = useState('');
  const [tagline, setTagline] = useState('');
  const [color, setColor] = useState('#D97706');
  const [tab, setTab] = useState('settings');
  const [showPreview, setShowPreview] = useState(false);

  // New Phase 3 state
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [features, setFeatures] = useState({ voice: true, offers: true, suggestions: true, proactive_nudge: true });
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [voicePersonality, setVoicePersonality] = useState('nova');
  const fileInputRef = useRef(null);

  // Live chat state
  const [chatMsgs, setChatMsgs] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatSession] = useState(() => `preview-${Date.now()}-${Math.random().toString(36).slice(2)}`);
  const chatEndRef = useRef(null);
  const [suggestedReplies, setSuggestedReplies] = useState([]);
  const [thinkingText, setThinkingText] = useState('');
  const [lastIntent, setLastIntent] = useState('');

  useEffect(() => { if (activeBranch) fetchStatus(); }, [activeBranch]);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatMsgs]);

  const fetchStatus = async () => {
    if (!activeBranch) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/conversational/status?branch_id=${activeBranch.id}`, authHeaders());
      if (res.data.exists && res.data.site) {
        setExists(true); setSiteData(res.data.site);
        const cfg = res.data.site.config || {};
        setName(cfg.restaurant_name || activeBranch.name);
        setTagline(cfg.tagline || '');
        setColor(cfg.colors?.primary || '#D97706');
        setWelcomeMessage(cfg.welcome_message || '');
        setLogoUrl(cfg.logo_url || '');
        setFeatures(cfg.features || { voice: true, offers: true, suggestions: true, proactive_nudge: true });
        setVoicePersonality(cfg.voice_personality || 'nova');
      } else {
        setExists(false); setSiteData(null);
        setName(activeBranch.name); setTagline(''); setColor('#D97706');
      }
    } catch { setExists(false); setSiteData(null); }
    finally { setLoading(false); }
  };

  const handleSetup = async () => {
    if (!activeBranch) return;
    setSaving(true);
    try {
      const res = await axios.post(`${API}/conversational/setup`, {
        branch_id: activeBranch.id, restaurant_name: name || activeBranch.name,
        tagline, primary_color: color, is_active: true,
        welcome_message: welcomeMessage, features, voice_personality: voicePersonality,
      }, authHeaders());
      if (res.data.success) {
        setSiteData(res.data.site); setExists(true);
        toast.success(exists ? t('convBot.updated') : t('convBot.activated'));
        if (chatMsgs.length === 0) initChat();
      }
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      toast.error(detail || t('convBot.failed'));
    }
    finally { setSaving(false); }
  };

  const handleDeactivate = async () => {
    if (!siteData) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/conversational/${siteData.id}`, authHeaders());
      setSiteData(prev => ({ ...prev, is_published: false }));
      toast.success(t('convBot.deactivated'));
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      toast.error(detail || t('convBot.failed'));
    }
    finally { setSaving(false); }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !activeBranch) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('branch_id', activeBranch.id);
      const res = await axios.post(`${API}/conversational/upload-logo`, fd, authHeaders());
      if (res.data.success) {
        setLogoUrl(res.data.logo_url);
        toast.success('تم رفع اللوجو بنجاح');
      }
    } catch { toast.error('فشل رفع اللوجو'); }
    finally { setUploading(false); }
  };

  const fetchStats = async () => {
    if (!activeBranch) return;
    setStatsLoading(true);
    try {
      const res = await axios.get(`${API}/conversational/stats?branch_id=${activeBranch.id}`, authHeaders());
      setStats(res.data);
    } catch { setStats(null); }
    finally { setStatsLoading(false); }
  };

  useEffect(() => { if (tab === 'stats' && activeBranch) fetchStats(); }, [tab, activeBranch]);

  const toggleFeature = (key) => {
    setFeatures(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const initChat = () => {
    setChatMsgs([{ role: 'assistant', text: welcomeMessage || t('convBot.chatWelcome', { name: name || 'المطعم' }) }]);
  };

  useEffect(() => { if (exists && siteData?.is_published && chatMsgs.length === 0) initChat(); }, [exists, siteData]);

  const sendChat = async (text) => {
    const msg = text || chatInput.trim();
    if (!msg || chatLoading || !activeBranch) return;
    setChatInput('');
    setSuggestedReplies([]);
    setChatMsgs(prev => [...prev, { role: 'user', text: msg }]);
    setChatLoading(true);
    setThinkingText(msg.includes('منيو') ? 'يتصفح المنيو...' : msg.includes('سلة') ? 'يراجع السلة...' : 'يفكّر...');
    try {
      const res = await axios.post(`${API}/public/smart-chat`, {
        branch_id: activeBranch.id, session_id: chatSession, message: msg, cart_items: [], quick_action: ''
      });
      setChatMsgs(prev => [...prev, { role: 'assistant', text: res.data.text, cards: res.data.rich_cards }]);
      if (res.data.suggested_replies?.length > 0) setSuggestedReplies(res.data.suggested_replies.slice(0, 3));
      if (res.data.intent) setLastIntent(res.data.intent);
    } catch {
      setChatMsgs(prev => [...prev, { role: 'assistant', text: t('conv.error') }]);
    } finally { setChatLoading(false); setThinkingText(''); }
  };

  const pageUrl = siteData ? `${API_URL}/site/${siteData.id}` : '';
  const isActive = siteData?.is_published === true;

  const copyLink = () => {
    if (!pageUrl) return;
    navigator.clipboard.writeText(pageUrl);
    setCopied(true); toast.success(t('convBot.linkCopied'));
    setTimeout(() => setCopied(false), 2000);
  };

  if (!activeBranch) return (
    <ModuleLayout moduleKey="ai">
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
        <MessageCircle className="w-12 h-12 text-muted-foreground mb-4" />
        <h2 className="text-lg font-semibold">{t('convBot.selectBranch')}</h2>
      </div>
    </ModuleLayout>
  );

  if (loading) return (
    <ModuleLayout moduleKey="ai"><div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div></ModuleLayout>
  );

  const tabs = [
    { id: 'settings', icon: Palette, label: t('convBot.settingsTab') },
    { id: 'stats', icon: BarChart3, label: 'إحصائيات' },
    { id: 'share', icon: Share2, label: t('convBot.shareTab') },
  ];

  return (
    <ModuleLayout moduleKey="ai">
      <div className="space-y-4" data-testid="conv-bot-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-bold text-xl md:text-2xl text-foreground flex items-center gap-2" style={{ fontFamily: 'Cairo, sans-serif' }}>
              <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
                <MessageCircle className="w-5 h-5" style={{ color }} />
              </div>
              {t('convBot.title')}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            {exists && isActive && (
              <Button
                variant="default"
                size="sm"
                onClick={() => setShowPreview(true)}
                className="rounded-full gap-1.5 text-xs h-8"
                style={{ background: 'linear-gradient(135deg, #D97706, #F59E0B)', border: 'none' }}
                data-testid="customer-preview-btn"
              >
                <Eye className="w-3.5 h-3.5" /> شوف كيف يشوفك عميلك
              </Button>
            )}
            {exists && isActive ? (
              <>
                <div className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-600">
                  <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" /> {t('convBot.active')}
                </div>
                <Button variant="outline" size="sm" onClick={handleDeactivate} disabled={saving} className="rounded-full gap-1 text-xs h-8" data-testid="conv-deactivate-btn">
                  <PowerOff className="w-3 h-3" /> {t('convBot.deactivateBtn')}
                </Button>
              </>
            ) : exists ? (
              <Button size="sm" onClick={handleSetup} disabled={saving} className="rounded-full gap-1 text-xs h-8" data-testid="conv-reactivate-btn">
                <Power className="w-3 h-3" /> {t('convBot.activateBtn')}
              </Button>
            ) : null}
          </div>
        </div>

        {/* Main: Split View */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4" style={{ minHeight: 'calc(100vh - 200px)' }}>

          {/* Right: Live Preview */}
          <div className="lg:col-span-3 order-1 lg:order-2">
            <Card className="h-full overflow-hidden border border-border/50 rounded-2xl flex flex-col" style={{ background: '#08080D' }}>
              <div className="flex items-center justify-between px-4 py-2.5 border-b" style={{ borderColor: 'rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.02)' }}>
                <div className="flex items-center gap-2">
                  {logoUrl ? (
                    <img src={logoUrl.startsWith('/') ? `${API_URL}${logoUrl}` : logoUrl} alt="logo" className="w-7 h-7 rounded-lg object-cover" />
                  ) : (
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${color}, ${color}CC)` }}>
                      <ChefHat className="w-3.5 h-3.5 text-white" />
                    </div>
                  )}
                  <div>
                    <div className="text-xs font-bold text-white">{name || 'المطعم'}</div>
                    <div className="text-[9px] text-white/30">{tagline || 'الصفحة التحادثية'}</div>
                  </div>
                </div>
                {pageUrl && (
                  <a href={pageUrl} target="_blank" rel="noopener noreferrer" className="text-[10px] text-white/30 hover:text-white/60 flex items-center gap-1">
                    <ExternalLink className="w-3 h-3" /> {t('convBot.openFull')}
                  </a>
                )}
              </div>

              {/* Chat area */}
              <div className="flex-1 overflow-y-auto p-3 space-y-2.5 custom-scrollbar" style={{ minHeight: 300, maxHeight: 'calc(100vh - 380px)' }}>
                {chatMsgs.length === 0 && !exists ? (
                  <div className="flex flex-col items-center justify-center h-full text-center py-12">
                    <Sparkles className="w-10 h-10 mb-3" style={{ color: `${color}60` }} />
                    <p className="text-sm text-white/40">{t('convBot.activateDesc')}</p>
                    <Button onClick={handleSetup} disabled={saving} className="mt-4 rounded-full text-xs gap-1" style={{ background: `linear-gradient(135deg, ${color}, ${color}CC)` }}>
                      <Power className="w-3 h-3" /> {t('convBot.activateNow')}
                    </Button>
                  </div>
                ) : (
                  <>
                    {chatMsgs.map((m, i) => (
                      <div key={i} className={`flex ${m.role === 'user' ? 'justify-start' : 'justify-end'} gap-2`}>
                        {m.role === 'assistant' && (
                          logoUrl ? (
                            <img src={logoUrl.startsWith('/') ? `${API_URL}${logoUrl}` : logoUrl} alt="" className="w-6 h-6 rounded-lg object-cover flex-shrink-0 mt-0.5" />
                          ) : (
                            <div className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: `linear-gradient(135deg, ${color}, ${color}BB)` }}>
                              <ChefHat className="w-3 h-3 text-white" />
                            </div>
                          )
                        )}
                        <div className="max-w-[85%]">
                          <div className={`px-3 py-2 rounded-xl text-xs leading-relaxed ${m.role === 'user' ? 'text-white rounded-br-sm' : 'text-white/90 rounded-bl-sm'}`}
                            style={{ background: m.role === 'user' ? `linear-gradient(135deg, ${color}, ${color}DD)` : 'rgba(255,255,255,0.06)', border: m.role === 'user' ? 'none' : '1px solid rgba(255,255,255,0.06)' }}>
                            {m.text}
                          </div>
                          {m.cards?.map((card, j) => (
                            <div key={j}><RichCardRenderer card={card} color={color} compact={true} onAddToCart={(item) => sendChat(`أضف ${item.name}`)} /></div>
                          ))}
                        </div>
                      </div>
                    ))}
                    {chatLoading && (
                      <div className="flex justify-end gap-2">
                        {logoUrl ? (
                          <img src={logoUrl.startsWith('/') ? `${API_URL}${logoUrl}` : logoUrl} alt="" className="w-6 h-6 rounded-lg object-cover" />
                        ) : (
                          <div className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${color}, ${color}BB)` }}>
                            <ChefHat className="w-3 h-3 text-white" />
                          </div>
                        )}
                        <div className="px-4 py-2.5 rounded-xl" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.06)' }}>
                          <div className="flex items-center gap-2">
                            <div className="flex gap-1">{[0,1,2].map(i => <div key={i} className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: color, animationDelay: `${i * 0.2}s` }} />)}</div>
                            {thinkingText && <span className="text-[9px] text-white/30">{thinkingText}</span>}
                          </div>
                        </div>
                      </div>
                    )}
                    <div ref={chatEndRef} />
                  </>
                )}
              </div>

              {/* Input area */}
              {exists && isActive && (
                <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                  {suggestedReplies.length > 0 && !chatLoading && (
                    <div className="flex gap-1.5 px-3 pt-2 overflow-x-auto custom-scrollbar">
                      {suggestedReplies.map((reply, i) => (
                        <button key={i} onClick={() => { setSuggestedReplies([]); sendChat(reply); }}
                          className="px-3 py-1 rounded-full text-[10px] font-semibold whitespace-nowrap transition-all"
                          style={{ background: `${color}15`, border: `1px solid ${color}30`, color }} data-testid={`suggested-reply-${i}`}>
                          {reply}
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-1.5 px-3 pt-2 overflow-x-auto custom-scrollbar">
                    {[{ key: 'menu', label: '🍽️ المنيو' }, { key: 'order', label: '🛒 أطلب' }, { key: 'branches', label: '📍 الموقع' }].map(q => (
                      <button key={q.key} onClick={() => sendChat(q.key === 'menu' ? 'وريني المنيو' : q.key === 'order' ? 'أبي أطلب' : 'وين موقعكم؟')}
                        disabled={chatLoading} className="px-3 py-1 rounded-full text-[10px] font-semibold whitespace-nowrap transition-colors"
                        style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.5)' }}>
                        {q.label}
                      </button>
                    ))}
                  </div>
                  <div className="flex gap-2 p-3">
                    <input value={chatInput} onChange={e => setChatInput(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') sendChat(); }}
                      placeholder={t('convBot.tryChat')} disabled={chatLoading}
                      className="flex-1 h-9 rounded-full px-4 text-xs outline-none"
                      style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: '#F0F0F5', fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}
                      data-testid="conv-preview-input" />
                    <button onClick={() => sendChat()} disabled={!chatInput.trim() || chatLoading}
                      className="w-9 h-9 rounded-full flex items-center justify-center transition-opacity"
                      style={{ background: `linear-gradient(135deg, ${color}, ${color}CC)`, opacity: (!chatInput.trim() || chatLoading) ? 0.4 : 1 }}
                      data-testid="conv-preview-send">
                      <Send className="w-3.5 h-3.5 text-white" />
                    </button>
                  </div>
                </div>
              )}
            </Card>
          </div>

          {/* Left: Settings Panel */}
          <div className="lg:col-span-2 order-2 lg:order-1 space-y-3">
            {/* Tab switcher */}
            <div className="flex gap-1 p-1 rounded-xl" style={{ background: 'rgba(0,0,0,0.03)' }}>
              {tabs.map(tb => (
                <button key={tb.id} onClick={() => setTab(tb.id)} data-testid={`tab-${tb.id}`}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold transition-all ${tab === tb.id ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground'}`}>
                  <tb.icon className="w-3.5 h-3.5" /> {tb.label}
                </button>
              ))}
            </div>

            {/* ===== SETTINGS TAB ===== */}
            {tab === 'settings' && (
              <div className="space-y-3">
                {/* Basic Settings */}
                <Card className="border border-border/50 rounded-xl">
                  <CardContent className="p-4 space-y-4">
                    <div className="flex items-center gap-2 text-xs font-bold text-foreground mb-1">
                      <Palette className="w-3.5 h-3.5 text-primary" /> الإعدادات الأساسية
                    </div>
                    {/* Logo Upload */}
                    <div>
                      <Label className="text-xs mb-1.5 block">لوجو المطعم</Label>
                      <div className="flex items-center gap-3">
                        <div className="w-14 h-14 rounded-xl border-2 border-dashed border-border flex items-center justify-center overflow-hidden cursor-pointer hover:border-primary/50 transition-colors"
                          onClick={() => fileInputRef.current?.click()} data-testid="logo-upload-area">
                          {logoUrl ? (
                            <img src={logoUrl.startsWith('/') ? `${API_URL}${logoUrl}` : logoUrl} alt="logo" className="w-full h-full object-cover rounded-lg" />
                          ) : (
                            <Image className="w-5 h-5 text-muted-foreground" />
                          )}
                        </div>
                        <div className="flex-1">
                          <Button variant="outline" size="sm" disabled={uploading || !exists} className="rounded-full gap-1 text-[10px] h-7"
                            onClick={() => fileInputRef.current?.click()} data-testid="logo-upload-btn">
                            {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                            {logoUrl ? 'تغيير اللوجو' : 'رفع لوجو'}
                          </Button>
                          <p className="text-[9px] text-muted-foreground mt-1">PNG أو JPG (حد أقصى 2MB)</p>
                        </div>
                        <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={handleLogoUpload} className="hidden" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs mb-1.5 block">{t('convBot.restaurantName')}</Label>
                      <Input value={name} onChange={e => setName(e.target.value)} placeholder={t('convBot.restaurantName')} className="h-9 text-sm rounded-lg" data-testid="conv-name-input" />
                    </div>
                    <div>
                      <Label className="text-xs mb-1.5 block">{t('convBot.taglineLabel')}</Label>
                      <Input value={tagline} onChange={e => setTagline(e.target.value)} placeholder={t('convBot.taglineLabel')} className="h-9 text-sm rounded-lg" data-testid="conv-tagline-input" />
                    </div>
                    <div>
                      <Label className="text-xs mb-1.5 block">{t('convBot.primaryColor')}</Label>
                      <div className="flex items-center gap-2">
                        <input type="color" value={color} onChange={e => setColor(e.target.value)} className="w-9 h-9 rounded-lg cursor-pointer border border-border" data-testid="conv-color-picker" />
                        <Input value={color} onChange={e => setColor(e.target.value)} className="w-24 h-9 text-xs font-mono rounded-lg" dir="ltr" />
                        <div className="flex gap-1.5">
                          {['#D97706', '#2563EB', '#059669', '#DC2626', '#7C3AED', '#0891B2'].map(c => (
                            <button key={c} onClick={() => setColor(c)} className="w-6 h-6 rounded-full border-2 transition-transform hover:scale-110"
                              style={{ backgroundColor: c, borderColor: color === c ? c : 'transparent' }} />
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Custom Greeting */}
                <Card className="border border-border/50 rounded-xl">
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-center gap-2 text-xs font-bold text-foreground">
                      <MessageSquare className="w-3.5 h-3.5 text-primary" /> رسالة الترحيب
                    </div>
                    <textarea value={welcomeMessage} onChange={e => setWelcomeMessage(e.target.value)}
                      placeholder="يا هلا وغلا! أنا مساعدك الذكي. تبي تشوف المنيو؟ تطلب شي؟"
                      rows={3} data-testid="welcome-message-input"
                      className="w-full px-3 py-2 rounded-lg border border-border text-sm bg-background resize-none outline-none focus:border-primary/50 transition-colors"
                      style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }} />
                    <p className="text-[9px] text-muted-foreground">اتركها فارغة لاستخدام الرسالة الافتراضية</p>
                  </CardContent>
                </Card>

                {/* Feature Toggles */}
                <Card className="border border-border/50 rounded-xl">
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-center gap-2 text-xs font-bold text-foreground">
                      <ToggleLeft className="w-3.5 h-3.5 text-primary" /> الميزات
                    </div>
                    {[
                      { key: 'voice', label: 'المحادثة الصوتية', desc: 'تفعيل إدخال وإخراج الصوت' },
                      { key: 'offers', label: 'العروض والخصومات', desc: 'عرض العروض للعملاء' },
                      { key: 'suggestions', label: 'اقتراحات الكومبو', desc: 'اقتراح إضافات ذكية للطلب' },
                      { key: 'proactive_nudge', label: 'التنبيهات الاستباقية', desc: 'تنبيه العميل عند التوقف' },
                    ].map(feat => (
                      <div key={feat.key} className="flex items-center justify-between py-1.5" data-testid={`feature-toggle-${feat.key}`}>
                        <div>
                          <div className="text-xs font-semibold text-foreground">{feat.label}</div>
                          <div className="text-[9px] text-muted-foreground">{feat.desc}</div>
                        </div>
                        <button onClick={() => toggleFeature(feat.key)} className="relative w-10 h-5 rounded-full transition-colors"
                          style={{ background: features[feat.key] ? color : 'rgba(0,0,0,0.1)' }}>
                          <div className="absolute top-0.5 transition-all rounded-full w-4 h-4 bg-white shadow-sm"
                            style={{ [features[feat.key] ? 'right' : 'left']: 2 }} />
                        </button>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                {/* Voice Personality */}
                <Card className="border border-border/50 rounded-xl">
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-center gap-2 text-xs font-bold text-foreground">
                      <Volume2 className="w-3.5 h-3.5 text-primary" /> شخصية الصوت
                    </div>
                    <p className="text-[10px] text-muted-foreground -mt-1">اختر صوت الوكيل عند الرد الصوتي على العملاء</p>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { id: 'verse',   emoji: '🤗', name: 'فيرس',   desc: 'ودود وشبابي (الافتراضي)' },
                        { id: 'echo',    emoji: '👨‍🍳', name: 'إيكو',   desc: 'عميق ومحترف' },
                        { id: 'shimmer', emoji: '👸', name: 'شيمر',   desc: 'أنثوي وراقي' },
                        { id: 'coral',   emoji: '🎭', name: 'كورال',  desc: 'مرح وحيوي' },
                        { id: 'alloy',   emoji: '🎯', name: 'ألوي',   desc: 'محايد ومتوازن' },
                        { id: 'ash',     emoji: '🌊', name: 'آش',     desc: 'ناعم وهادئ' },
                      ].map(v => (
                        <button key={v.id} onClick={() => setVoicePersonality(v.id)}
                          data-testid={`voice-personality-${v.id}`}
                          className="flex items-center gap-2.5 p-2.5 rounded-xl text-right transition-all"
                          style={{
                            border: voicePersonality === v.id ? `1.5px solid ${color}` : '1px solid rgba(0,0,0,0.08)',
                            background: voicePersonality === v.id ? `${color}10` : 'rgba(0,0,0,0.02)',
                          }}>
                          <span style={{ fontSize: 20 }}>{v.emoji}</span>
                          <div className="min-w-0">
                            <div className="text-[11px] font-bold text-foreground">{v.name}</div>
                            <div className="text-[9px] text-muted-foreground">{v.desc}</div>
                          </div>
                          {voicePersonality === v.id && (
                            <div className="w-3.5 h-3.5 rounded-full flex items-center justify-center flex-shrink-0 mr-auto" style={{ background: color }}>
                              <Check className="w-2 h-2 text-white" />
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                    <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg" style={{ background: `${color}08`, border: `1px solid ${color}15` }}>
                      <Mic className="w-3 h-3 flex-shrink-0" style={{ color }} />
                      <span className="text-[10px]" style={{ color }}>
                        الصوت المحدد: <strong>{{'verse':'فيرس',echo:'إيكو',shimmer:'شيمر',coral:'كورال',alloy:'ألوي',ash:'آش'}[voicePersonality] || voicePersonality}</strong> — يُطبّق مع OpenAI Realtime API
                      </span>
                    </div>
                  </CardContent>
                </Card>

                {/* Save Button */}
                <Button onClick={handleSetup} disabled={saving || !name.trim()} className="w-full rounded-full gap-1.5 h-9 text-xs" data-testid="conv-save-btn">
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                  {exists ? t('convBot.updateBtn') : t('convBot.activateBtn')}
                </Button>
              </div>
            )}

            {/* ===== STATS TAB ===== */}
            {tab === 'stats' && (
              <div className="space-y-3">
                {statsLoading ? (
                  <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
                ) : stats ? (
                  <>
                    {/* Summary Cards */}
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { label: 'المحادثات اليوم', value: stats.today_conversations, icon: MessageCircle, color: '#2563EB' },
                        { label: 'الطلبات اليوم', value: stats.today_orders, icon: ShoppingCart, color: '#059669' },
                        { label: 'إجمالي المحادثات', value: stats.total_conversations, icon: MessageSquare, color: '#7C3AED' },
                        { label: 'معدل التحويل', value: `${stats.conversion_rate}%`, icon: TrendingUp, color: '#D97706' },
                      ].map((stat, i) => (
                        <Card key={i} className="border border-border/50 rounded-xl" data-testid={`stat-card-${i}`}>
                          <CardContent className="p-3">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${stat.color}12` }}>
                                <stat.icon className="w-3.5 h-3.5" style={{ color: stat.color }} />
                              </div>
                            </div>
                            <div className="text-lg font-bold text-foreground" style={{ fontFamily: 'Cairo, sans-serif' }}>{stat.value}</div>
                            <div className="text-[10px] text-muted-foreground font-medium">{stat.label}</div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>

                    {/* Detailed Stats */}
                    <Card className="border border-border/50 rounded-xl">
                      <CardContent className="p-4 space-y-3">
                        <div className="flex items-center gap-2 text-xs font-bold text-foreground">
                          <BarChart3 className="w-3.5 h-3.5 text-primary" /> تفاصيل الإحصائيات
                        </div>
                        {[
                          { label: 'محادثات هذا الأسبوع', value: stats.week_conversations },
                          { label: 'إجمالي الرسائل', value: stats.total_messages },
                          { label: 'متوسط الرسائل/محادثة', value: stats.avg_messages_per_conversation },
                          { label: 'جلسات الدفع', value: stats.total_checkouts },
                          { label: 'المدفوعة', value: stats.paid_checkouts },
                          { label: 'إيرادات الدفع الإلكتروني', value: `${stats.total_revenue} ر.س` },
                        ].map((row, i) => (
                          <div key={i} className="flex items-center justify-between py-1 border-b border-border/30 last:border-0">
                            <span className="text-[11px] text-muted-foreground">{row.label}</span>
                            <span className="text-[12px] font-bold text-foreground">{row.value}</span>
                          </div>
                        ))}
                      </CardContent>
                    </Card>

                    <Button variant="outline" size="sm" onClick={fetchStats} className="w-full rounded-full gap-1 text-xs h-8" data-testid="refresh-stats-btn">
                      <RefreshCw className="w-3 h-3" /> تحديث الإحصائيات
                    </Button>
                  </>
                ) : (
                  <Card className="border border-border/50 rounded-xl">
                    <CardContent className="p-6 text-center text-muted-foreground text-sm">لا توجد إحصائيات بعد</CardContent>
                  </Card>
                )}
              </div>
            )}

            {/* ===== SHARE TAB ===== */}
            {tab === 'share' && exists && isActive && (
              <div className="space-y-3">
                <Card className="border border-border/50 rounded-xl">
                  <CardContent className="p-4 space-y-2">
                    <div className="flex items-center gap-2 text-xs font-semibold"><Share2 className="w-3.5 h-3.5 text-primary" /> {t('convBot.shareLink')}</div>
                    <div className="flex gap-1.5">
                      <Input value={pageUrl} readOnly className="flex-1 h-8 text-[10px] font-mono bg-muted/50 rounded-lg" dir="ltr" data-testid="conv-share-link" />
                      <Button variant="outline" size="sm" onClick={copyLink} className="h-8 px-3 rounded-lg gap-1 text-[10px]" data-testid="conv-copy-link">
                        {copied ? <Check className="w-3 h-3 text-emerald-500" /> : <Copy className="w-3 h-3" />}
                        {copied ? t('convBot.copiedBtn') : t('convBot.copyBtn')}
                      </Button>
                    </div>
                    <a href={pageUrl} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-1.5 w-full py-1.5 rounded-lg text-[10px] font-semibold text-primary hover:bg-primary/5 transition-colors border border-border/50">
                      <ExternalLink className="w-3 h-3" /> {t('convBot.openNewTab')}
                    </a>
                  </CardContent>
                </Card>
                <Card className="border border-border/50 rounded-xl">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 text-xs font-semibold mb-3"><QrCode className="w-3.5 h-3.5 text-primary" /> {t('convBot.qrCode')}</div>
                    <div className="flex items-center gap-4">
                      <div className="bg-white p-2 rounded-lg" data-testid="conv-qr-code">
                        <QRCode value={pageUrl} size={90} />
                      </div>
                      <div className="flex-1 space-y-2">
                        <p className="text-[10px] text-muted-foreground leading-relaxed">{t('convBot.qrDescription')}</p>
                        <Button variant="outline" size="sm" className="rounded-full gap-1 text-[10px] h-7 w-full" data-testid="conv-download-qr"
                          onClick={() => {
                            const svg = document.querySelector('[data-testid="conv-qr-code"] svg');
                            if (svg) {
                              const data = new XMLSerializer().serializeToString(svg);
                              const canvas = document.createElement('canvas');
                              canvas.width = 400; canvas.height = 400;
                              const ctx = canvas.getContext('2d');
                              const img = new window.Image();
                              img.onload = () => { ctx.fillStyle = '#fff'; ctx.fillRect(0,0,400,400); ctx.drawImage(img,0,0,400,400); const a = document.createElement('a'); a.download = 'qr-code.png'; a.href = canvas.toDataURL('image/png'); a.click(); };
                              img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(data)));
                            }
                          }}>
                          <Download className="w-3 h-3" /> {t('convBot.downloadPng')}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {tab === 'share' && (!exists || !isActive) && (
              <Card className="border border-border/50 rounded-xl">
                <CardContent className="p-6 text-center text-muted-foreground text-sm">{t('convBot.activateFirst')}</CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
      <CustomerPreviewModal open={showPreview} onClose={() => setShowPreview(false)} />
    </ModuleLayout>
  );
}
